import './index.scss';
import EventEmitter from 'events';

import Buttons from './buttons';
import Overlay from './overlay';

/** @module VideoThing */
export default class VideoThing extends EventEmitter {
  /**
   * Create a videoThing.
   */

  constructor(targetElem, locations, destinations, settings) {
    super();

    this.settings = {
      startLocation: 'a1',
      videoAspectRatio: 16 / 9,
      hotspotsRelativeToVideo: true,
      navigationRelativeToVideo: true,
      assetBase: '',

      transitionOverlay: {
        color: '#fff',
        transitionDuration: 700,
      },
      ...settings,
    };

    this.debug = window.location.search.indexOf('debug=true') > -1;
    this.destinations = destinations;
    this.locations = locations;
    this.history = [];
    this.init(targetElem);
  }

  async init(targetElem) {
    const settings = this.settings;

    // basic markup

    const html = `
        <div class="stage">
          <div class="controls controls--viewport_relative"></div>
          <div class="controls controls--video_relative"></div>
          <div class="layers"></div>
        </div>
        `;

    this.elem = targetElem;

    targetElem.id = 'videothing';
    targetElem.innerHTML = html;

    // prepare stage
    this.stage = this.elem.querySelector('.stage');

    window.addEventListener('resize', () => {});

    document.addEventListener('DOMContentLoaded', () => {});

    // show first location
    const startLocation = this.getLocationById(settings.startLocation);
    this.markupLocation(startLocation);
    if (startLocation.type === 'loop' || startLocation.type === 'interruptible-loop') {
      const loop = await this.getLocationLoop(settings.startLocation);

      loop.play();
    }
    this.enterLocation(settings.startLocation);
  }

  // navigation visibility
  async showNavigation() {
    this.navigationHidden = false;
    this.elem.querySelectorAll('.controls').forEach((layer) => {
      layer.classList.remove('controls--navigation-hidden');
    });

    await this.currentButtons.show('navigation');
  }

  async hideNavigation() {
    this.navigationHidden = true;
    await this.currentButtons.hide('navigation');

    this.elem.querySelectorAll('.controls').forEach((layer) => {
      layer.classList.add('controls--navigation-hidden');
    });
  }

  toggleNavigation() {
    if (this.navigationHidden) {
      this.showNavigation();
    } else {
      this.hideNavigation();
    }
  }

  // hotspots visibility
  async showHotspots() {
    this.hotspotsHidden = false;
    this.elem.querySelectorAll('.controls').forEach((layer) => {
      layer.classList.remove('controls--hotspots-hidden');
    });
    await this.currentButtons.show('hotspot');
  }

  async hideHotspots() {
    this.hotspotsHidden = true;
    await this.currentButtons.hide('hotspot');
    this.elem.querySelectorAll('.controls').forEach((layer) => {
      layer.classList.add('controls--hotspots-hidden');
    });
  }

  toggleHotspots() {
    if (this.hotspotsHidden) {
      this.showHotspots();
    } else {
      this.hideHotspots();
    }
  }

  // menus visibility
  async showMenus() {
    this.menusHidden = false;
    this.elem.querySelectorAll('.controls').forEach((layer) => {
      layer.classList.remove('controls--menus-hidden');
    });
    await this.currentButtons.show('menu');
  }

  async hideMenus() {
    this.menusHidden = true;
    await this.currentButtons.hide('menu');
    this.elem.querySelectorAll('.controls').forEach((layer) => {
      layer.classList.add('controls--menus-hidden');
    });
  }

  toggleMenus() {
    if (this.menusHidden) {
      this.showMenus();
    } else {
      this.hideMenus();
    }
  }

  // constrols visibility
  showControls() {
    this.showHotspots();
    this.showNavigation();
  }

  hideControls() {
    this.hideHotspots();
    this.hideNavigation();
  }

  markupLocation(location) {
    const layersContainer = this.elem.querySelector('.layers');
    const existing = layersContainer.querySelector(`#location-${location.id}`);

    // do nothing if location already in dom
    if (existing) return;

    let template;
    switch (location.type) {
      case 'image':
        template = `<div class="layer layer--location" id="location-${location.id}" style="background-image: url(${this.settings.assetBase + location.src})"></div>`;
        break;
      case 'loop':
      case 'interruptible-loop':
        template = `<div class="layer layer--location video" id="location-${location.id}">
                        <video ${location.sound ? '' : 'muted'} playsinline src="${this.settings.assetBase + location.src}"></video>
                      </div>`;

        break;
      default:
    }

    layersContainer.insertAdjacentHTML('afterbegin', template);
  }

  preloadDestinations(destinations) {
    const layersContainer = this.elem.querySelector('.layers');

    // delete old destinations
    layersContainer.querySelectorAll('.layer--destination').forEach((elem) => {
      elem.parentNode.removeChild(elem);
    });

    // add new destinations
    destinations.forEach((destination) => {
      const template = `
      <div class="layer layer--destination video" data-from="${destination.from}"  data-to="${destination.to}">
        <video muted playsinline src="${this.settings.assetBase}${destination.src}"}></video>
      </div>`;
      layersContainer.insertAdjacentHTML('afterbegin', template);
    });
  }

  showLocation(locationId) {
    const layersContainer = this.elem.querySelector('.layers');

    // 1. show location to locations
    this.constructor.makeLayerCurrent(layersContainer.querySelector(`#location-${locationId}`));

    // 2. be prepared for next move
    setTimeout(() => {
      const destinations = this.getDestinationsAtLocation(locationId);
      this.preloadDestinations(
        destinations.map((destination) => {
          destination.from = locationId;
          return destination;
        }),
      );
    }, 250);
  }

  getLocationById(locationId) {
    return this.locations.find((location) => location.id === locationId);
  }

  getDestinationsAtLocation(locationId) {
    return this.destinations.filter((destination) => destination.from === locationId);
  }

  findDestinationBetweenLocations(startLocationId, targetLocationId) {
    return this.getDestinationsAtLocation(startLocationId).find((dest) => targetLocationId === dest.to);
  }

  getCurrentLocation() {
    return this.history[this.history.length - 1];
  }

  async goToLocation(locationId) {
    if (this.transitioning || !locationId) {
      // keine location id
      // oder gerade am transitionieren
      return false;
    }
    const currentLocationId = this.getCurrentLocation();
    this.leaveLocation(this.getCurrentLocation());
    this.transitioning = true;
    await this.transition(locationId);
    this.transitioning = false;
    this.enterLocation(locationId);
    // remove old location if is not same
    if (locationId !== currentLocationId) this.removeLocationFromDom(currentLocationId);
    return true;
  }

  removeLocationFromDom(id) {
    const elem = this.elem.querySelector(`.layers #location-${id}`);
    elem.parentNode.removeChild(elem);
  }

  async stopCurrentLocationLoop() {
    return new Promise((reslove, reject) => {
      const location = this.getLocationById(this.getCurrentLocation());

      if (location.type === 'loop') {
        const videoElem = this.elem.querySelector(`#location-${location.id} video`);

        // seeked event should fire when the video starts over
        // works in chrome, still needs to be tested

        const onSeeked = () => {
          videoElem.removeEventListener('seeked', onSeeked);
          videoElem.pause();
          reslove();
        };
        videoElem.addEventListener('seeked', onSeeked);
      } else if (location.type === 'interruptible-loop') {
        const videoElem = this.elem.querySelector(`#location-${location.id} video`);
        videoElem.pause();
        reslove();
      } else {
        reslove();
      }
    });
  }

  async getLocationLoop(locationId) {
    // resolved ein canplay video

    return new Promise((resolve, reject) => {
      const location = this.getLocationById(locationId);
      this.markupLocation(location);
      const video = this.elem.querySelector(`#location-${location.id} video`);

      video.currentTime = 0;
      video.loop = true;

      const onCanPlay = () => {
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('loadedmetadata', onCanPlay);
        resolve(video);
      };
      video.addEventListener('canplay', onCanPlay);
      video.addEventListener('loadedmetadata', onCanPlay);

      if (video.readyState >= 1) resolve(video);
    });
  }

  async leaveLocation(locationId) {
    this.emit('location_left', locationId);
    await this.currentButtons.clear();

    // -> button animation zu ende
  }

  addLocationToHistory(locationId) {
    // do not store same location twice in a row
    if (locationId !== this.history[this.history.length - 1]) {
      this.history.push(locationId);
      if (this.history.length > 100) this.history.shift();
    }
  }

  async goBack() {
    if (this.history.length <= 1) {
      throw new Error('the end. you are where you came from');
    }

    const lastLocation = this.history[this.history.length - 2];
    const transition = await this.goToLocation(lastLocation);

    if (transition) {
      this.history.pop();
      this.history.pop();
    }

    return this.history;
  }

  async enterLocation(locationId) {
    const location = this.getLocationById(locationId);

    this.showLocation(locationId);

    this.currentButtons = new Buttons(this.settings.assetBase, location.buttons, (e, item) => {
      switch (item.type) {
        case 'navigation':
          this.emit('location_button_clicked', e, item.target);
          this.goToLocation(item.target);
          break;
        case 'hotspot':
          this.emit('custom_button_clicked', e, item.id);
          break;
        case 'menu':
          this.emit('menu_button_clicked', e, item);
          break;
        default:
          console.log(`unknown button type: ${item.type}`);
      }
    });

    this.emit('location_reached', locationId);
    this.addLocationToHistory(locationId);

    await this.currentButtons.show();

    this.emit('location_animations_complete', locationId);
  }

  static makeLayerCurrent(newCurrentLayer) {
    const currentLayer = document.querySelector('.layer--current');
    if (currentLayer) currentLayer.classList.remove('layer--current');
    newCurrentLayer.classList.add('layer--current');
  }

  async transition(to) {
    const location = this.getLocationById(to);

    console.info(`Transition to ${to}`, location);

    const from = this.getCurrentLocation();
    const transitionPromises = [];
    let loopAtTargetLocation = null;

    if (location.type === 'loop' || location.type === 'interruptible-loop') {
      transitionPromises.push(this.getLocationLoop(to));
    }

    return new Promise(async (resolve, reject) => {
      const destination = this.findDestinationBetweenLocations(from, to);

      if (destination) {
        // video
        const destinationLayer = this.elem.querySelector(`.layer[data-to="${destination.to}"][data-from="${from}"]`);

        const destinationVideo = destinationLayer.querySelector('video');

        if (!destinationVideo) reject(new Error('Destination does not exist in DOM.'));

        const onPlaying = () => {
          destinationVideo.removeEventListener('playing', onPlaying);
        };

        const onCanPlay = async () => {
          if (destinationVideo.paused) {
            console.info('Destination ready', destination);

            transitionPromises.push(this.stopCurrentLocationLoop());

            await Promise.all(transitionPromises).then((values) => {
              // wait for current loop end
              // ... and target ready
              loopAtTargetLocation = values[0];
            });

            this.constructor.makeLayerCurrent(destinationLayer);
            destinationVideo.play();
          }
        };

        const onEnded = async () => {
          // am ziel angekommen
          destinationVideo.removeEventListener('ended', onEnded);
          if (loopAtTargetLocation) loopAtTargetLocation.play();
          resolve();
        };

        destinationVideo.addEventListener('playing', onPlaying);
        destinationVideo.addEventListener('canplay', onCanPlay);
        destinationVideo.addEventListener('loadedmetadata', onCanPlay);

        if (destination.type === 'video') {
          destinationVideo.currentTime = 0;
          destinationVideo.addEventListener('ended', onEnded);
          onCanPlay();
        } else {
          // play video from start- to stop timeout
          // and show it when ready (makeLayerCurrent)
          let playing = false;
          const onTimeupdate = (e) => {
            if (!playing) {
              playing = true;

              // console.log(`start time: ${destinationVideo.currentTime}`);

              destinationVideo.addEventListener('playing', onPlaying);
            }

            // wait for end time
            if (destinationVideo.currentTime >= destination.stop) {
              console.log(`stopped at: ${destinationVideo.currentTime}`);

              // dieser wert ist nicht exakt. weiß nicht wie das besser zu machen wäre.
              // animation frame?

              // am ziel angekommen
              destinationVideo.pause();
              destinationVideo.removeEventListener('timeupdate', onTimeupdate);
              onEnded();
            }
          };

          // set start time
          destinationVideo.currentTime = destination.start ? destination.start : 0;

          // wait for timeupdate -> then do stuff
          destinationVideo.addEventListener('timeupdate', onTimeupdate);
        }
      } else {
        console.info(`There is no direct destination from ${from} to ${to} -> using fallback`);

        // there is no direct destination -> fallback via overlay
        const overlay = new Overlay(this.elem.querySelector('.layers'), this.settings.transitionOverlay.color, this.settings.transitionOverlay.transitionDuration);

        await overlay.show();
        this.showLocation(to);

        await Promise.all(transitionPromises).then((values) => {
          // wait for target ready
          loopAtTargetLocation = values[0];
          if (loopAtTargetLocation) loopAtTargetLocation.play();
        });

        await overlay.hide();
        resolve();
      }
    });
  }
}
